import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FeedbackDialog } from '../components/FeedbackDialog';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  cursor: 'pointer',
};

const dangerButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-danger, #e53e3e)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-danger, #e53e3e)',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

export function Holidays() {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formLocationId, setFormLocationId] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: holidays = [], isLoading } = useQuery({
    queryKey: ['public-holidays', year],
    queryFn: () => api.getPublicHolidays({ year }),
  });

  const { data: locationsList = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.getLocations(),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; date: string; location_id?: number | null }) =>
      api.createPublicHoliday(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-holidays'] });
      resetForm();
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updatePublicHoliday(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-holidays'] });
      resetForm();
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deletePublicHoliday(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['public-holidays'] }),
  });

  function resetForm() {
    setShowForm(false);
    setEditingId(null);
    setFormName('');
    setFormDate('');
    setFormLocationId('');
  }

  function startEdit(holiday: any) {
    setEditingId(holiday.id);
    setFormName(holiday.name);
    setFormDate(holiday.date?.slice(0, 10) || '');
    setFormLocationId(holiday.location_id ? String(holiday.location_id) : '');
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data = {
      name: formName,
      date: formDate,
      location_id: formLocationId ? Number(formLocationId) : null,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, data });
    } else {
      createMutation.mutate(data);
    }
  }

  const [pendingDelete, setPendingDelete] = useState<{ id: number; name: string } | null>(null);
  const [pendingCopy, setPendingCopy] = useState(false);

  const copyMutation = useMutation({
    mutationFn: () => api.copyHolidaysFromYear(year - 1, year),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['public-holidays'] });
      setPendingCopy(false);
      if (data.copied === 0) {
        setErrorMsg(`No new holidays copied — ${year} already has all holidays from ${year - 1}.`);
      }
    },
    onError: (err: Error) => {
      setPendingCopy(false);
      setErrorMsg(err.message);
    },
  });

  const isPast = (date: string) => new Date(date.slice(0, 10) + 'T23:59:59') < new Date();

  const upcomingHolidays = holidays.filter((h: any) => !isPast(h.date));
  const pastHolidays = holidays.filter((h: any) => isPast(h.date));

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Public Holidays</h1>
          <p
            style={{
              color: 'var(--color-text-muted)',
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Bookings are blocked on these days. Manage Queensland public holidays and custom
            closures.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ ...inputStyle, width: 'auto' }}
          >
            {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            style={{
              ...primaryButtonStyle,
              backgroundColor: 'transparent',
              border: '1px solid var(--color-accent)',
              color: 'var(--color-accent)',
            }}
            onClick={() => setPendingCopy(true)}
          >
            Copy from {year - 1}
          </button>
          <button
            style={primaryButtonStyle}
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + Add Holiday
          </button>
        </div>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
          <h2 style={{ margin: '0 0 var(--spacing-3)' }}>
            {editingId ? 'Edit Holiday' : 'New Holiday'}
          </h2>
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 'var(--spacing-3)',
              alignItems: 'end',
            }}
          >
            <div>
              <label style={labelStyle}>Name</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Christmas Day"
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Location</label>
              <select
                value={formLocationId}
                onChange={(e) => setFormLocationId(e.target.value)}
                style={inputStyle}
              >
                <option value="">All Locations</option>
                {locationsList.map((loc: any) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 'var(--spacing-2)' }}>
              <button
                type="submit"
                style={primaryButtonStyle}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  ...primaryButtonStyle,
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text)',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      ) : holidays.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--spacing-8)' }}>
          <p style={{ color: 'var(--color-text-muted)' }}>
            No public holidays configured for {year}.
          </p>
        </div>
      ) : (
        <>
          {/* Upcoming */}
          {upcomingHolidays.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
              <h2 style={{ margin: '0 0 var(--spacing-3)', fontSize: 'var(--font-size-base)' }}>
                Upcoming ({upcomingHolidays.length})
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {upcomingHolidays.map((h: any) => (
                  <HolidayRow
                    key={h.id}
                    holiday={h}
                    locations={locationsList}
                    onEdit={() => startEdit(h)}
                    onDelete={() => setPendingDelete({ id: h.id, name: h.name })}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past */}
          {pastHolidays.length > 0 && (
            <div style={cardStyle}>
              <h2
                style={{
                  margin: '0 0 var(--spacing-3)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-text-muted)',
                }}
              >
                Past ({pastHolidays.length})
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pastHolidays.map((h: any) => (
                  <HolidayRow
                    key={h.id}
                    holiday={h}
                    locations={locationsList}
                    onEdit={() => startEdit(h)}
                    onDelete={() => setPendingDelete({ id: h.id, name: h.name })}
                    dimmed
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={pendingCopy}
        title={`Copy holidays from ${year - 1}?`}
        message={`This will copy all public holidays from ${year - 1} into ${year}. Existing holidays in ${year} won't be duplicated.`}
        confirmLabel="Copy"
        onConfirm={() => copyMutation.mutate()}
        onCancel={() => setPendingCopy(false)}
      />

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title={`Delete ${pendingDelete?.name ?? 'holiday'}?`}
        message="This public holiday will be removed and bookings will be allowed on this date again."
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />

      <FeedbackDialog
        isOpen={!!errorMsg}
        title="Could not save holiday"
        message={errorMsg || ''}
        tone="error"
        onClose={() => setErrorMsg(null)}
      />
    </div>
  );
}

function HolidayRow({
  holiday,
  locations,
  onEdit,
  onDelete,
  dimmed,
}: {
  holiday: any;
  locations: any[];
  onEdit: () => void;
  onDelete: () => void;
  dimmed?: boolean;
}) {
  const locationName = holiday.location_id
    ? locations.find((l: any) => l.id === holiday.location_id)?.name ||
      `Location ${holiday.location_id}`
    : 'All Locations';

  const dateStr = typeof holiday.date === 'string' ? holiday.date.slice(0, 10) : '';
  const dateObj = new Date(dateStr + 'T00:00:00');
  const dayName = dateObj.toLocaleDateString('en-AU', { weekday: 'short' });
  const dateFormatted = dateObj.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--spacing-2) var(--spacing-3)',
        borderRadius: 'var(--radius-default)',
        opacity: dimmed ? 0.5 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
        <span
          style={{
            width: 48,
            textAlign: 'center',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: 'var(--color-accent)',
            textTransform: 'uppercase',
          }}
        >
          {dayName}
        </span>
        <div>
          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{holiday.name}</div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            {dateFormatted} · {locationName}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
        <button
          onClick={onEdit}
          style={{
            padding: 'var(--spacing-1) var(--spacing-3)',
            backgroundColor: 'transparent',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-default)',
            color: 'var(--color-text)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          Edit
        </button>
        <button onClick={onDelete} style={dangerButtonStyle}>
          Delete
        </button>
      </div>
    </div>
  );
}
